import {
  cacheKeys,
  useCurrentAccount,
  useDeleteAccount,
} from '@gripp/shared-logic';
import {
  AvatarUpload,
  Button,
  Colors,
  colors,
  ProfileForm,
  SaveFormBottom,
  Spacing,
  TextInput as TextInputCropCircle,
  tokens,
  typography,
  useProfileForm,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MediaPicker } from '../../../shared/components/media/mediaPicker';
import { useUnsavedChangesBlocker } from '../../../shared/hooks/useUnsavedChangesBlocker';
import { getMediaBaseUrl } from '../../../shared/mediaUtils';

export const SettingsProfile = () => {
  const { setColumnTitle, setColumnOverlay } = useSlideLayout();
  const mediaUrl = getMediaBaseUrl();
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const queryClient = useQueryClient();

  // Avatar is staged here and committed with the rest of the profile on save:
  // while a pick is uploading `avatarUploading` gates the save button, and the
  // uploaded media id is threaded into the profile mutation via `getAvatarId`.
  const [stagedAvatarId, setStagedAvatarId] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useProfileForm({
    getAvatarId: () => stagedAvatarId,
    onSaved: () => {
      setStagedAvatarId(null);
      setAvatarUploading(false);
      // Refresh the current account's cached avatar so the nav + page reflect
      // the new picture. Same mechanism asset edits use (useQueryClient +
      // cacheKeys factory), scoped to this account's detail key.
      if (account?.id) {
        void queryClient.invalidateQueries({
          queryKey: cacheKeys('account').detail(account.id),
        });
      }
    },
  });
  const { mutateAsync: deleteAccount } = useDeleteAccount();

  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const hasAvatarChange = !!stagedAvatarId;
  const canSaveProfile = isValid && (isDirty || hasAvatarChange) && !avatarUploading;

  useUnsavedChangesBlocker({
    isDirty: isDirty || hasAvatarChange,
    isValid,
    onSave: handleSubmit,
  });

  useEffect(() => {
    setColumnTitle(t('preferences.settings.profile'));
  }, [setColumnTitle, t]);

  const closeDeleteOverlay = useCallback(() => {
    if (isDeleting) return;
    setDeleteVisible(false);
    setDeleteReason('');
  }, [isDeleting]);

  const handleDeleteAccount = useCallback(async () => {
    if (!account?.id) return;
    setIsDeleting(true);
    try {
      await deleteAccount(
        { input: { reason: deleteReason } },
        {
          onSettled: () => {
            setIsDeleting(false);
            setDeleteVisible(false);
            setDeleteReason('');
          },
        }
      );
    } catch {
      setIsDeleting(false);
    }
  }, [account?.id, deleteAccount, deleteReason]);

  useEffect(() => {
    if (!deleteVisible) {
      setColumnOverlay(null);
      return;
    }

    setColumnOverlay(
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          {isDeleting ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={Colors.primary} />
            </View>
          ) : (
            <>
              <View style={styles.sheetHeader}>
                <Text style={typography.title.smallEmphasised}>
                  {t('deleteAccount.header')}
                </Text>
                <Text style={typography.body.small}>
                  {t('deleteAccount.cannotUndone')}
                </Text>
              </View>
              <TextInputCropCircle
                placeholder={t('deleteAccount.reasonInputNoOptional')}
                textArea
                onChangeText={setDeleteReason}
                value={deleteReason}
              />
              <View style={styles.sheetButtons}>
                <Button
                  variant="rectangular"
                  state="warning"
                  onPress={handleDeleteAccount}
                  style={styles.deleteConfirmButton}
                  size="large"
                >
                  {t('asset.delete.confirm')}
                </Button>
                <Button
                  variant="rectangular"
                  onPress={closeDeleteOverlay}
                  size="large"
                >
                  {t('topNav.customAlert.cancel')}
                </Button>
              </View>
            </>
          )}
        </View>
      </View>
    );

    return () => {
      setColumnOverlay(null);
    };
  }, [
    deleteVisible,
    deleteReason,
    isDeleting,
    handleDeleteAccount,
    closeDeleteOverlay,
    setColumnOverlay,
    t,
  ]);

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.contentContainer}
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
      >
        <SaveFormBottom
          title={t('avatar.menu.profile')}
          onSubmit={handleSubmit}
          isDirty={isDirty || hasAvatarChange}
          isValid={isValid}
          hasBorder={true}
          bottomBar={() => null}
        >
          <ProfileForm
            control={control}
            mediaUrl={mediaUrl}
            imagePicker={MediaPicker}
            useCropCircle
            inputBackgroundColor={Colors.primaryGrayBackground}
            avatarSlot={
              <AvatarUpload
                mediaUrl={mediaUrl}
                imagePicker={MediaPicker}
                onUploadingChange={setAvatarUploading}
                onStagedMediaChange={setStagedAvatarId}
              />
            }
          />
        </SaveFormBottom>
        <View style={styles.contentActions}>
          <Button
            style={styles.saveButton}
            onPress={canSaveProfile ? handleSubmit : undefined}
            state={canSaveProfile ? 'active' : 'default'}
            size="large"
            disabled={!canSaveProfile}
          >
            {t('asset.save')}
          </Button>
          <Button
            variant="rectangular"
            state="outline"
            style={styles.deleteButton}
            onPress={() => setDeleteVisible(true)}
            disabled={isDeleting}
            size="default"
          >
            {t('deleteAccount.button')}
          </Button>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
  },
  contentActions: {
    marginTop: Spacing.baseMargin.base,
    gap: tokens.spacing.sm,
  },
  saveButton: {
    borderRadius: tokens.borderRadius.sm,
    width: '100%',
    height: 48,
  },
  deleteButton: {
    borderWidth: 0,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: tokens.colors.brand.white,
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing['2xl'],
    paddingBottom: tokens.spacing['2xl'],
    gap: tokens.spacing.lg,
  },
  sheetHeader: {
    alignItems: 'center',
    gap: 4,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheetButtons: {
    gap: tokens.spacing.md,
  },
  deleteConfirmButton: {
    backgroundColor: colors.semantic.error,
  },
});
